import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { LifeBuoy, BookOpen, MessageCircle, Plus, ArrowLeft, Activity, Handshake, Briefcase, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export const Route = createFileRoute("/support/")({
  component: SupportPortal,
  head: () => ({
    meta: [
      { title: "Support — CoderLity" },
      { name: "description", content: "Open a support ticket, browse the knowledge base, check system status, or reach sales and partnerships." },
      { property: "og:title", content: "Support — CoderLity" },
      { property: "og:description", content: "Open a support ticket, browse the knowledge base, check system status, or reach sales and partnerships." },
      { property: "og:url", content: "/support" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/support" }],
  }),
});

type Ticket = {
  id: string; subject: string; body: string; status: string; priority: string;
  category: string | null; created_at: string; updated_at: string;
};
type Reply = { id: string; ticket_id: string; body: string; is_internal: boolean; created_at: string };

type NavItem = { slug: string; title: string; desc: string; icon: typeof LifeBuoy };
const help: NavItem[] = [
  { slug: "support-center", title: "Support Center", desc: "Explore support center", icon: LifeBuoy },
  { slug: "knowledge-base", title: "Knowledge Base", desc: "Explore knowledge base", icon: BookOpen },
  { slug: "system-status", title: "System Status", desc: "Explore system status", icon: Activity },
];
const contact: NavItem[] = [
  { slug: "contact-sales", title: "Contact Sales", desc: "Explore contact sales", icon: Briefcase },
  { slug: "partnerships", title: "Partnerships", desc: "Explore partnerships", icon: Handshake },
];

function Group({ title, items }: { title: string; items: NavItem[] }) {
  return (
    <section className="py-8">
      <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.slug}
              to="/support/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function SupportPortal() {
  const { user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [openNew, setOpenNew] = useState(false);

  if (selected) return <TicketView id={selected} onBack={() => setSelected(null)} />;

  return (
    <PageShell>
      <PageHero
        eyebrow="Support"
        title="We're here to help"
        sub="Open a ticket, track your requests, browse the knowledge base, or talk to sales and partnerships."
      />
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 space-y-10">
          <div className="grid gap-4 sm:grid-cols-3">
            <Link to="/kb" className="group rounded-xl border border-border bg-card p-5 hover:border-primary/40">
              <BookOpen className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-semibold group-hover:text-primary">Knowledge base</h3>
              <p className="mt-1 text-sm text-muted-foreground">Browse guides by category.</p>
            </Link>
            <button onClick={() => setOpenNew(true)} className="group rounded-xl border border-border bg-card p-5 text-left hover:border-primary/40">
              <LifeBuoy className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-semibold group-hover:text-primary">Open a ticket</h3>
              <p className="mt-1 text-sm text-muted-foreground">Get help from our support team.</p>
            </button>
            <Link to="/contact" className="group rounded-xl border border-border bg-card p-5 hover:border-primary/40">
              <MessageCircle className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-semibold group-hover:text-primary">Contact us</h3>
              <p className="mt-1 text-sm text-muted-foreground">Reach out via chat or email.</p>
            </Link>
          </div>

          <Group title="Help" items={help} />
          <Group title="Contact" items={contact} />

          {user ? (
            <MyTickets onSelect={setSelected} onNew={() => setOpenNew(true)} />
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">
                <Link to="/login" className="text-primary underline">Sign in</Link> to open a ticket and track your requests.
              </p>
            </div>
          )}
        </div>
      </section>

      <NewTicketDialog open={openNew} onClose={() => setOpenNew(false)} />
    </PageShell>
  );
}

function MyTickets({ onSelect, onNew }: { onSelect: (id: string) => void; onNew: () => void }) {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["my_tickets", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets").select("*").eq("user_id", user!.id).order("updated_at", { ascending: false });
      if (error) throw error;
      return data as Ticket[];
    },
  });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">My tickets</h2>
        <Button size="sm" onClick={onNew}><Plus className="mr-1 h-4 w-4" />New ticket</Button>
      </div>
      <div className="rounded-xl border border-border bg-card divide-y">
        {isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && !data?.length && <div className="p-6 text-center text-sm text-muted-foreground">No tickets yet.</div>}
        {data?.map((t) => (
          <button key={t.id} onClick={() => onSelect(t.id)} className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-muted/40">
            <div className="min-w-0">
              <div className="truncate font-medium">{t.subject}</div>
              <div className="text-xs text-muted-foreground">Updated {formatDistanceToNow(new Date(t.updated_at), { addSuffix: true })}</div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{t.priority}</Badge>
              <Badge>{t.status}</Badge>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function NewTicketDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ subject: "", body: "", priority: "normal", category: "" });

  const create = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Please sign in");
      if (!form.subject.trim() || !form.body.trim()) throw new Error("Subject and message are required");
      const { error } = await supabase.from("support_tickets").insert({
        user_id: user.id,
        subject: form.subject.trim().slice(0, 200),
        body: form.body.trim().slice(0, 10000),
        priority: form.priority as any,
        category: form.category || null,
        status: "open" as any,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Ticket submitted");
      qc.invalidateQueries({ queryKey: ["my_tickets"] });
      setForm({ subject: "", body: "", priority: "normal", category: "" });
      onClose();
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Open a support ticket</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={200} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Category (optional)</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} maxLength={64} /></div>
          </div>
          <div><Label>Message</Label><Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={10000} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketView({ id, onBack }: { id: string; onBack: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");

  const { data: ticket } = useQuery({
    queryKey: ["ticket", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_tickets").select("*").eq("id", id).single();
      if (error) throw error;
      return data as Ticket;
    },
  });
  const { data: replies } = useQuery({
    queryKey: ["ticket_replies", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ticket_replies").select("*").eq("ticket_id", id).eq("is_internal", false).order("created_at");
      if (error) throw error;
      return data as Reply[];
    },
  });

  const send = useMutation({
    mutationFn: async () => {
      if (!reply.trim()) throw new Error("Reply cannot be empty");
      const { error } = await supabase.from("ticket_replies").insert({
        ticket_id: id, author_id: user?.id, body: reply.trim().slice(0, 10000), is_internal: false,
      });
      if (error) throw error;
      await supabase.from("support_tickets").update({ updated_at: new Date().toISOString(), status: "open" as any }).eq("id", id);
    },
    onSuccess: () => {
      setReply("");
      qc.invalidateQueries({ queryKey: ["ticket_replies", id] });
      qc.invalidateQueries({ queryKey: ["my_tickets"] });
      toast.success("Reply sent");
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <PageShell>
      <section className="py-10">
        <div className="mx-auto max-w-3xl px-4 lg:px-8 space-y-6">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
          {!ticket ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : (
            <>
              <div>
                <h1 className="text-2xl font-semibold">{ticket.subject}</h1>
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">{ticket.priority}</Badge>
                  <Badge>{ticket.status}</Badge>
                  <span>Opened {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}</span>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="whitespace-pre-wrap text-sm">{ticket.body}</p>
              </div>
              {replies?.map((r) => (
                <div key={r.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="mb-2 text-xs text-muted-foreground">{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</div>
                  <p className="whitespace-pre-wrap text-sm">{r.body}</p>
                </div>
              ))}
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <Label>Reply</Label>
                <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} maxLength={10000} />
                <div className="flex justify-end">
                  <Button onClick={() => send.mutate()} disabled={send.isPending}>Send reply</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}
